@if (board(); as board) {
  <!-- PAR-01: the whole record accepts a native drag as a Stitch to THIS board, as the deck preview does. -->
  <div class="board-drop-target relative flex h-full flex-col overflow-hidden"
    appDroppable
    appDroppableHoverClass="drag-over"
    [appDroppableDisabled]="board.deleted"
    [appDroppablePredicate]="canDropStitch"
    (appDroppableDrop)="dropStitch($event)">
    <div class="drop-area-border" aria-hidden="true"></div>
    <!-- Heading and meta row: prototype Boards.tsx:301 on Slides' heading (slides-preview.component.html:36-122). -->
    <app-board-record-heading [board]="board" (openEditor)="openEditor()" (closed)="closePreview()" />

    <!-- State strip (prototype Boards.tsx:301 end, BoardRecord.css:2): the native Trash banner stays; Archived, Snoozed, Follow up and Template
         follow as the prototype's single line, with its text buttons through the existing store writes. -->
    @if (board.deleted) {
      <div class="mx-8 mb-2 mt-2 flex items-center justify-between rounded border border-border bg-primary-50 px-3 py-2 text-sm">
        <span>This board is in Trash.</span>
        <app-button2 appearance="primary-text" size="s" (click)="store.deleteBoard(board.id)">Restore</app-button2>
      </div>
    } @else if (board.archived || board.snoozed || board.followUp || board.isTemplate) {
      <div class="flex shrink-0 items-center gap-[10px] border-b border-[#f0e1b6] bg-[#fff8e8] px-[14px] py-[5px] text-[11px]" data-testid="board-state-strip">
        @if (board.archived) { Archived }
        @else if (board.snoozed) { Snoozed until {{ board.snoozedUntil | date: 'MMM d, y · h:mm a' }} }
        @else if (board.followUp) { Marked for follow up }
        @else { Saved template }
        @if (board.isTemplate) { <app-button2 appearance="primary-text" size="xs" data-testid="state-use-template" [disabled]="copying()" (click)="useTemplate(board)">Use template</app-button2> }
        @if (board.followUp) { <app-button2 appearance="primary-text" size="xs" data-testid="state-complete-follow-up" (click)="store.setFollowUp(board.id, null)">Complete follow up</app-button2> }
        @if (board.snoozed) { <app-button2 appearance="primary-text" size="xs" data-testid="state-unsnooze" (click)="store.setSnooze(board.id, null)">Unsnooze</app-button2> }
      </div>
    }

    <!-- The record tabs, wired as slides-preview.component.html wires them: the tab strip with the blue Knowledge control, the one shared
         Knowledge block (Knots, Tags, Connections), then the tab content. Comments stays mounted once opened, so a
         tab change never destroys a typed or failed draft (R05-CX-80). -->
    <div class="skin-detail-tabs !mt-0 flex h-[42px] min-w-0 shrink-0 flex-row items-center gap-6 border-b border-gray-200 bg-white px-8">
      <app-tabs class="min-w-0 flex-1 self-end" appearance="sapphire" size="m" [values]="tabs()" [value]="activeTab()" (valueChange)="selectTab($event)" />
      <stch-knowledge-button data-testid="knowledge-toggle" [active]="displayKnowledge()" (click)="toggleKnowledge()" />
    </div>

    @if (activeTab() === 'stitch' && hasKnowledgeHubAccess()) {
      <app-entity-hub-search [contactId]="board.id" />
    }

    <app-board-knowledge
      data-testid="record-knowledge"
      [class.hidden]="!displayKnowledge() || (activeTab() === 'stitch' && !hasKnowledgeHubAccess())"
      [board]="board"
      [hubMode]="activeTab() === 'stitch' && hasKnowledgeHubAccess()"
    />

    <div
      class="relative min-h-0 flex-1 overscroll-contain"
      [class.overflow-y-auto]="!(activeTab() === 'stitch' && hasKnowledgeHubAccess())"
      [class.overflow-hidden]="activeTab() === 'stitch' && hasKnowledgeHubAccess()"
    >
      <!-- The Board tab is first and is the canvas preview, in the slot where slides-preview.component.html:183-190 puts app-bento-preview.
           Mounted only while it is the tab, so the lazy canvas is not kept alive behind other tabs. -->
      @if (activeTab() === 'board') {
        <app-board-record-tab class="absolute inset-0" [boardId]="board.id" [board]="board" [title]="board.title" [trashed]="board.deleted" (openEditor)="openEditor()" (counts)="setCounts(board.id, $event)" />
      }
      <div class="flex flex-col gap-4 px-8 py-6" [class.hidden]="activeTab() !== 'details'"><app-board-details [board]="board" [showKnowledge]="false" /></div>
      <!-- History (owner request, Sheets `sheet-form.component.html:497-499` `@case ('history')` with a lazy wrapper): R08's version history inline, its code loaded only when the tab opens (`@defer`). The menu's Version history still opens the dialog. -->
      @if (activeTab() === 'history') {
        @defer {
          <app-board-version-history class="block h-full" [embedded]="true" [boardUid]="board.id" />
        }
      }
      @if (activeTab() === 'attachments') {
        <app-board-attachments [board]="board" (listStateChange)="attachmentsListState.set($event)" />
      }
      <!-- Stitch is mounted only while it is the tab, as Slides' preview and editor do: leaving it ends the native hub, which resets its
           query, filters, type and sort and keeps the marker selection (knowledge-hub.md 14.10, R05-CX-66). Discussion drafts need no such hold. -->
      @if (activeTab() === 'stitch') {
        <div class="h-full">
          @if (permissionsLoaded()) {
            <app-board-connections [board]="board" [hubMode]="hasKnowledgeHubAccess()" [showCreate]="false" (listStateChange)="stitchListState.set($event)" />
          }
        </div>
      }
      @if (visited().has('comments')) {
        <div class="block px-8 py-6" data-testid="board-comments" [class.hidden]="activeTab() !== 'comments'">
          @if (annotationList.failed()) {
            <div class="flex items-center gap-3 rounded border border-border p-3 text-sm" role="alert" data-testid="annotations-failed">
              <span>Couldn’t read the annotations{{ annotationList.loaded() ? ', so what is shown may be out of date' : '' }}.</span>
              <app-button2 appearance="secondary" size="s" data-testid="annotations-retry" (click)="annotationList.reload()">Retry</app-button2>
            </div>
          }
          <stch-comments [stitch]="stitchSummary()" [readOnly]="board.deleted" scope="general" [fenceCompletion]="true" [active]="activeTab() === 'comments'" [threadPins]="threadPins()" [pinsStale]="annotationList.failed() || annotationList.loading()" />
        </div>
      }
    </div>
    <!-- Footer (prototype Boards.tsx:320, BoardBreadcrumbs.tsx/.css, globals.css:1,15): "Boards app" (a link to All boards) > the board title, 12px #637791 with the
         title in ink; the scene counts at the right, 10px. -->
    <div class="flex h-[49px] shrink-0 items-center justify-between gap-3 border-t border-gray-200 bg-offwhite px-4 text-[10px] text-[#637791]" data-testid="board-record-footer">
      <nav class="board-breadcrumbs flex min-w-0 flex-1 items-center text-[12px]" aria-label="Breadcrumb">
        <ol class="m-0 flex min-w-0 list-none items-center gap-2 p-0">
          <li class="flex shrink-0 items-center gap-2"><button type="button" class="board-breadcrumb-link" data-testid="breadcrumb-app" (click)="showAllBoards()">Boards app</button></li>
          <li class="flex min-w-0 items-center gap-2"><app-icon class="shrink-0" name="sapphire-chevron-right" [size]="14" aria-hidden="true" /><span class="min-w-0 truncate text-[#0f1f33]" aria-current="page" data-testid="breadcrumb-title">{{ board.title || 'Untitled board' }}</span></li>
        </ol>
      </nav>
      @if (footerCounts(); as c) { <span class="shrink-0" data-testid="footer-counts">{{ c.objects }} structured objects · {{ c.frames }} frames</span> }
    </div>
  </div>
} @else {
  <app-board-empty-state class="block h-full w-full" />
}
